'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { inviteTeamMember } from '../hooks/useTeam'
import { useEvents } from '@/features/events/hooks/useEvents'
import { Button } from '@planner/ui'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@planner/ui'
import { Input } from '@planner/ui'
import { Label } from '@planner/ui'
import { Textarea } from '@planner/ui'

interface TeamInviteFormProps {
  onClose: () => void
  allowPaidRoles: boolean
  onUpsellRequest?: () => void
  onInviteSuccess?: (member: { email: string; role: string; full_name: string | null; status: string }) => void
  defaultRole?: 'klient' | 'pracownik'
}

export function TeamInviteForm({ onClose, allowPaidRoles, onUpsellRequest, onInviteSuccess, defaultRole = 'klient' }: TeamInviteFormProps) {
  const router = useRouter()
  const { events } = useEvents()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'klient' | 'pracownik'>(defaultRole)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [demoInviteLink, setDemoInviteLink] = useState<string | null>(null)
  
  // Event selection state (only for clients)
  const [eventSelection, setEventSelection] = useState<'existing' | 'new' | 'none' | null>('none')
  const [selectedEventId, setSelectedEventId] = useState('')
  const [newEventData, setNewEventData] = useState({
    title: '',
    event_date: '',
    number_of_guests: 1,
    description: '',
  })

  // Reset event selection when role changes
  useEffect(() => {
    if (role !== 'klient') {
      setEventSelection(null)
      setSelectedEventId('')
      setNewEventData({ title: '', event_date: '', number_of_guests: 1, description: '' })
    } else {
      // Default to 'none' when switching to client (no event required)
      setEventSelection('none')
    }
  }, [role])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    if (role === 'pracownik' && !allowPaidRoles) {
      setError('Dodawanie pracowników wymaga planu Enterprise')
      onUpsellRequest?.()
      return
    }

    // Validate event selection for clients (only if event is selected)
    if (role === 'klient' && eventSelection) {
      if (eventSelection === 'existing' && !selectedEventId) {
        setError('Musisz wybrać wydarzenie')
        return
      }
      if (eventSelection === 'new') {
        if (!newEventData.title || !newEventData.event_date) {
          setError('Wypełnij wszystkie wymagane pola wydarzenia')
          return
        }
      }
    }

    setSending(true)
    setError(null)
    setSuccess(false)

    try {
      const eventId = eventSelection === 'existing' ? selectedEventId : undefined
      const newEvent = eventSelection === 'new' ? newEventData : undefined
      
      const result = await inviteTeamMember(email, role, eventId, newEvent)
      if (result.success) {
        setSuccess(true)
        // Notify parent about the new invited member (for demo mode)
        onInviteSuccess?.({
          email,
          role,
          full_name: null,
          status: 'invited',
        })
        
        // For demo mode, show the invite link instead of sending email
        if (result.demoMode && result.token) {
          const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''
          setDemoInviteLink(`${baseUrl}/team/invite/${result.token}`)
        } else {
          setEmail('')
          setTimeout(() => {
            onClose()
            router.refresh()
          }, 1500)
        }
      } else {
        setError('Błąd podczas wysyłania zaproszenia')
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSending(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Zaproś użytkownika</CardTitle>
        <CardDescription>
          Wyślij zaproszenie do pracownika lub klienta przez email
        </CardDescription>
      </CardHeader>
      <CardContent>
        {success ? (
          <div className="text-center py-4">
            <div className="text-green-600 font-medium mb-2">
              {demoInviteLink ? 'Zaproszenie utworzone (Demo)' : 'Zaproszenie zostało wysłane!'}
            </div>
            {demoInviteLink ? (
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  W trybie demo email nie jest wysyłany. Użyj poniższego linku:
                </div>
                <div className="bg-muted rounded-md p-3">
                  <code className="text-xs break-all">{demoInviteLink}</code>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(demoInviteLink)
                  }}
                >
                  Kopiuj link
                </Button>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Przekierowywanie...
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="pracownik@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Rola</Label>
              <select
                id="role"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={role}
                onChange={(e) => {
                  const nextRole = e.target.value
                  if (nextRole === 'pracownik' && !allowPaidRoles) {
                    setRole('klient')
                    setError('Dodawanie pracowników wymaga planu Enterprise')
                    onUpsellRequest?.()
                    return
                  }
                  setRole(nextRole)
                  setError(null)
                }}
              >
                <option value="klient">Klient</option>
                <option value="pracownik">
                  Pracownik {allowPaidRoles ? '' : '(wymaga Enterprise)'}
                </option>
              </select>
            </div>

            {/* Event selection for clients */}
            {role === 'klient' && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                <Label>Wydarzenie (opcjonalnie)</Label>
                <div className="space-y-3">
                  <div className="flex flex-col gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="eventSelection"
                        value="none"
                        checked={eventSelection === 'none'}
                        onChange={(e) => setEventSelection(e.target.value as 'none')}
                        className="cursor-pointer"
                      />
                      <span className="text-sm">Bez wydarzenia (klient zrobi rezerwację później)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="eventSelection"
                        value="existing"
                        checked={eventSelection === 'existing'}
                        onChange={(e) => setEventSelection(e.target.value as 'existing')}
                        className="cursor-pointer"
                      />
                      <span className="text-sm">Wybierz istniejące wydarzenie</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="eventSelection"
                        value="new"
                        checked={eventSelection === 'new'}
                        onChange={(e) => setEventSelection(e.target.value as 'new')}
                        className="cursor-pointer"
                      />
                      <span className="text-sm">Utwórz nowe wydarzenie</span>
                    </label>
                  </div>

                  {eventSelection === 'existing' && (
                    <div className="space-y-2">
                      <Label htmlFor="eventSelect">Wybierz wydarzenie</Label>
                      <select
                        id="eventSelect"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={selectedEventId}
                        onChange={(e) => setSelectedEventId(e.target.value)}
                        required
                      >
                        <option value="">-- Wybierz wydarzenie --</option>
                        {events
                          .filter(e => e.status !== 'anulowana')
                          .map((event) => (
                            <option key={event.id} value={event.id}>
                              {event.title} - {new Date(event.event_date).toLocaleDateString('pl-PL')}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}

                  {eventSelection === 'new' && (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="eventTitle">Tytuł wydarzenia *</Label>
                        <Input
                          id="eventTitle"
                          value={newEventData.title}
                          onChange={(e) => setNewEventData({ ...newEventData, title: e.target.value })}
                          placeholder="np. Wesele"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eventDate">Data wydarzenia *</Label>
                        <Input
                          id="eventDate"
                          type="datetime-local"
                          value={newEventData.event_date}
                          onChange={(e) => setNewEventData({ ...newEventData, event_date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eventGuests">Liczba gości</Label>
                        <Input
                          id="eventGuests"
                          type="number"
                          min="1"
                          value={newEventData.number_of_guests}
                          onChange={(e) => setNewEventData({ ...newEventData, number_of_guests: parseInt(e.target.value) || 1 })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eventDescription">Opis (opcjonalnie)</Label>
                        <Textarea
                          id="eventDescription"
                          value={newEventData.description}
                          onChange={(e) => setNewEventData({ ...newEventData, description: e.target.value })}
                          placeholder="Dodatkowe informacje o wydarzeniu"
                          rows={3}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="text-sm text-destructive">{error}</div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Anuluj
              </Button>
              <Button type="submit" disabled={sending || !email}>
                {sending ? 'Wysyłanie...' : 'Wyślij zaproszenie'}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  )
}

